import {
  Box,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  SelectChangeEvent,
  Tooltip,
} from '@mui/material';
import { getNameWithCode } from '../../services';
import { InfoDialogIcon } from './InfoDialogIcon';
import { gridLayout } from '../SectionLayout';
import { useTranslation } from 'react-i18next';
import { OptionalControl, Price } from '..';
import {
  checkIsOptionReadonly,
  getPropertyValue,
} from '../../services/ConfigurationDataHelperFunctions';
import { EConfigitProperty, EJustification, EVariableType } from '../../data/Constants';
import type { ISingleValueSelectControlProps, IConfigurationValue } from '../../../types';
import type { IFeatureOrFamily } from '../../../types/IHelperTypes';

/**
 * Renders a dropdown which allows to select only one value.
 * @param {ISingleValueSelectControlProps} props the properties for the dropdown component
 * @returns {JSX.Element} the dropdown component
 */
export const SingleValueSelectControl = ({
  featureFlags,
  values,
  value,
  onChange,
  onOptionalChange,
  variable,
  disabled,
  showCode,
}: ISingleValueSelectControlProps) => {
  const { t } = useTranslation();
  const readOnlyTooltipText = t('tooltip.readOnlyTooltipText');

  const onInternalChange = (e: SelectChangeEvent) => onChange?.(e.target.value);
  const assignedValue = values?.find((v) => v.assigned);
  const readOnly =
    assignedValue?.assigned === EJustification.Rule ? checkIsOptionReadonly(assignedValue) : false;
  const setReadonly = (val: IConfigurationValue) => {
    const isPasswordProtected = getPropertyValue<boolean>(
      val,
      EConfigitProperty.IsPasswordProtected,
      EVariableType.Boolean
    );
    return readOnly || checkIsOptionReadonly(val) || isPasswordProtected;
  };

  return (
    <RadioGroup
      aria-labelledby="dynamic-input-control"
      name="single-select-control-group"
      value={value}
      onChange={onInternalChange}
      className="w-100"
    >
      <Grid container size={gridLayout.fullWidth}>
        {values?.map((v) => {
          const isFeatureReadOnly = setReadonly(v);
          /*
            If hidden return null. Return null will not show any input control for particular variable in UI.
            It doesn't have any impact on configuration summary, price or any other functionality of application
          */
          if (v.hide || v.suppressed) {
            return null;
          }
          return (
            <Grid
              key={v.value}
              container
              className={v.incompatible ? 'incompatible' : ''}
              sx={{ alignItems: 'stretch' }}
              size={gridLayout.fullWidth}
            >
              <Grid
                container
                sx={{ justifyContent: 'left', alignItems: 'center' }}
                size={{
                  xs: gridLayout.xs.input,
                  sm: gridLayout.sm.input,
                }}
              >
                <Tooltip
                  title={
                    isFeatureReadOnly || disabled
                      ? readOnlyTooltipText
                      : getNameWithCode(v as IFeatureOrFamily, showCode)
                  }
                >
                  <Box>
                    <FormControlLabel
                      className={v.isSearchValueMatched ? 'SearchValueMatched' : ''}
                      label={getNameWithCode(v as IFeatureOrFamily, showCode)}
                      value={v.value}
                      control={<Radio className="paddingTopBottom" />}
                      disabled={disabled ? true : isFeatureReadOnly}
                      sx={{
                        alignItems: 'center',
                        wordBreak: 'break-word',
                        whiteSpace: 'normal',
                      }}
                    />
                  </Box>
                </Tooltip>
              </Grid>
              <Grid
                container
                sx={{ alignItems: 'center', justifyContent: 'center' }}
                size={{
                  xs: gridLayout.xs.infoIcon,
                  sm: gridLayout.sm.infoIcon,
                }}
              >
                <InfoDialogIcon variable={v as IFeatureOrFamily} />
              </Grid>
              <Grid
                size={{
                  xs: gridLayout.xs.qty,
                  sm: gridLayout.sm.qty,
                }}
              ></Grid>
              <Grid
                container
                sx={{ alignItems: 'center', justifyContent: 'center' }}
                size={{
                  xs: gridLayout.xs.optional,
                  sm: gridLayout.sm.optional,
                }}
              >
                <OptionalControl
                  featureFamily={variable}
                  onOptionalChange={onOptionalChange}
                  option={v}
                  disabled={disabled ? true : isFeatureReadOnly}
                  featureFlag={featureFlags?.Optional}
                />
              </Grid>
              <Grid
                container
                sx={{ alignItems: 'right', justifyContent: 'right' }}
                size={{
                  xs: gridLayout.xs.price,
                  sm: gridLayout.sm.price,
                }}
              >
                <Box sx={{ display: { xs: 'inline', sm: 'none' }, textAlign: 'center' }}>
                  {t('labels.listPrice')}:&nbsp;
                </Box>
                <Price price={v.price} isBundled={v.isBundled} />
              </Grid>
            </Grid>
          );
        })}
      </Grid>
    </RadioGroup>
  );
};
